import PosMenuTop from "@/Components/PosMenu/PosMenuTop";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import TablesMap from "@/Components/Tables/TablesMap";
import { useSetPosOrderTable } from "@/shared/atoms/usePosOrderTable";
import usePosTableFilter, {
  filterPosTables,
  posTableDisplaysOptions,
  posTableFilterOptions,
} from "@/shared/atoms/usePosTableFilter";
import useCommon from "@/shared/hooks/useCommon";
import useActiveOrdersSessionsQuery from "@/shared/queries/orderssessions/useActiveOrdersSessionsQuery";
import useRestaurantMap from "@/shared/queries/restaurantMap/useRestaurantMap";
import useRestaurantMaps from "@/shared/queries/restaurantMap/useRestaurantMaps";
import { asArray } from "@/shared/utils/helpers";
import { useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";
import { useSetPosOrderCurrentAction } from "@/shared/atoms/usePosOrderCurrentAction";
import useDefaultAction from "@/Components/PosMenuAction/useDefaultAction";
import useIsWaiter from "@/shared/hooks/useIsWaiter";
import WaiterTables from "@/Pages/Auth/Waiter/WaiterTables";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";

const Tables = () => {
  const [ref, bounds] = useMeasure();
  const { settings } = usePosSettingsHook();
  const [showMap, setShowMap] = useState(settings.is_map_view ?? true);

  useEffect(() => {
    setShowMap(settings.is_map_view);
  }, [settings.is_map_view]);
  const t = useCommon();
  const [tableFilter, setTableFilter] = usePosTableFilter();
  const {
    isPending: isSessionsPending,
    data: sessions,
    refetch,
  } = useActiveOrdersSessionsQuery();
  const [mapId, setMap] = useState(undefined);
  const { data: maps, isPending: isRestaurantsMapsPending } =
    useRestaurantMaps();
  const chosenMap = useMemo(
    () => asArray(maps)?.find((option) => option.item_id === mapId),
    [mapId, maps]
  );
  const { data: map, isPending } = useRestaurantMap(mapId, mapId !== undefined);
  const setCurrentAction = useSetPosOrderCurrentAction();
  const setTable = useSetPosOrderTable();
  const setDefaultAction = useDefaultAction();

  useEffect(() => {
    if (map) {
      setCurrentAction({
        name: "enterTableNumber",
        action: (tableNumber: any) => {
          const table = map?.tables?.find(
            (t) => t.table_number === Number(tableNumber)
          );
          table && setTable(table);
        },
      });
    }
    return () => {
      setDefaultAction();
    };
  }, [map]);

  useEffect(() => {
    if (mapId === undefined && maps?.length > 0) {
      setMap(maps[0].item_id);
    }
  }, [maps, mapId]);

  const filtered = useMemo(
    () => filterPosTables(map?.tables, sessions, tableFilter),
    [map?.tables, tableFilter, sessions]
  );

  useEffect(() => {
    refetch();
  }, []);

  return (
    <div className="mt-8 flex w-full flex-1 flex-col">
      {isRestaurantsMapsPending || isSessionsPending ? (
        <Spinner />
      ) : (
        <div className="flex w-full flex-1 flex-col">
          <div className="flex w-full flex-wrap gap-4">
            {showMap ? (
              <>
                <div className="basis-[32%] flex-grow md:basis-auto">
                  <Select
                    menuPlacement="bottom"
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPortalTarget={document.getElementById("root")}
                    options={maps ?? []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.item_id}
                    label={t("chooseUrMap")}
                    value={chosenMap}
                    onChange={(e) => {
                      setMap(e.item_id);
                    }}
                  />
                </div>

                <div className="basis-[32%] flex-grow md:basis-auto">
                  <Select
                    menuPlacement="bottom"
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPortalTarget={document.getElementById("root")}
                    options={posTableFilterOptions}
                    getOptionLabel={(option) => t(option.label)}
                    getOptionValue={(option) => option.value}
                    label={t("tableFilter")}
                    value={posTableFilterOptions.find(
                      (option) => option.value === tableFilter
                    )}
                    onChange={(e) => {
                      setTableFilter(e.value);
                    }}
                  />
                </div>
              </>
            ) : null}
            <div className="basis-[32%] flex-grow md:basis-auto">
              <Select
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.getElementById("root")}
                options={posTableDisplaysOptions}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                label={t("display")}
                value={posTableDisplaysOptions.find(
                  (option) => option.value === showMap
                )}
                onChange={(e) => {
                  setShowMap(e.value);
                }}
              />
            </div>
          </div>
          {mapId !== undefined && isPending ? (
            <Spinner />
          ) : showMap ? (
            <div
              ref={ref}
              className="relative h-216 w-full overflow-hidden md:h-auto md:min-h-120 md:flex-1"
            >
              <div className="absolute inset-0">
                <TablesMap
                  background_color={map?.background_color}
                  readOnly
                  unavailableTables={sessions.flatMap(
                    (s) => s.current_table_ids
                  )}
                  onTableClick={(table) => {
                    setTable(table);
                  }}
                  width={bounds.width}
                  height={bounds.height}
                  tables={filtered}
                />
              </div>
            </div>
          ) : (
            <div className="my-8">
              <WaiterTables setTable={(table) => setTable(table)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PosTables = () => {
  const isWaiter = useIsWaiter();

  return (
    <>
      {isWaiter ? (
        <div className="flex h-full min-h-0 flex-1 flex-col">
          <WaiterTables />
        </div>
      ) : (
        <div className="flex flex-1 flex-col">
          <PosMenuTop />
          <Tables />
        </div>
      )}
    </>
  );
};

export default PosTables;
